import React, { useEffect, useState } from 'react';
import { REQUEST_STATUSES, STATUS_STYLE, CATEGORIES } from '../data/beraniRequests';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const CAT_NAME = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.name]));

const BeraniRequestsAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/berani/requests?limit=300`);
      const d = await r.json();
      setItems(d.requests || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = items
    .filter((i) => statusFilter === 'all' ? true : i.status === statusFilter)
    .filter((i) => catFilter === 'all' ? true : i.category === catFilter)
    .filter((i) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        i.request_number?.toLowerCase().includes(q) ||
        i.requester_name?.toLowerCase().includes(q) ||
        i.requester_email?.toLowerCase().includes(q) ||
        i.request_type?.toLowerCase().includes(q) ||
        i.description?.toLowerCase().includes(q)
      );
    });

  const stats = REQUEST_STATUSES.reduce((s, st) => ({ ...s, [st]: items.filter((i) => i.status === st).length }), {});

  const patchItem = async (id, patch) => {
    const r = await fetch(`${API}/berani/requests/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
    if (!r.ok) { alert('Chyba aktualizace'); return null; }
    const detail = await (await fetch(`${API}/berani/requests/${id}`)).json();
    setSelected(detail);
    setItems((prev) => prev.map((x) => x.request_id === id ? detail : x));
    return detail;
  };

  const sendReply = async (id, message, new_status) => {
    const r = await fetch(`${API}/berani/requests/${id}/reply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message, new_status }) });
    if (!r.ok) { alert('Chyba odeslání odpovědi'); return; }
    const detail = await (await fetch(`${API}/berani/requests/${id}`)).json();
    setSelected(detail);
    setItems((prev) => prev.map((x) => x.request_id === id ? detail : x));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="berani-requests-admin">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold">SPRÁVA · BERANI GASTRO</div>
            <h1 className="text-2xl font-black text-slate-900 mt-1">Požadavky</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/admin" className="text-sm text-slate-600 hover:text-slate-900">← Zpět na správu</a>
            <button onClick={load} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold">Obnovit</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-6">
          {REQUEST_STATUSES.map((s) => {
            const st = STATUS_STYLE[s];
            const active = statusFilter === s;
            return (
              <button key={s} data-testid={`stat-${s}`} onClick={() => setStatusFilter(active ? 'all' : s)} className={`p-4 rounded-xl text-left border transition-all ${active ? 'border-slate-900 shadow' : 'border-slate-200 hover:border-slate-400'} bg-white`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${st.dot}`} />
                  <div className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase">{s}</div>
                </div>
                <div className="text-2xl font-black text-slate-900 mt-1">{stats[s]}</div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="mb-4 rounded-xl bg-white border border-slate-200 p-4 shadow-sm flex flex-col md:flex-row gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hledat: číslo, jméno, e-mail, typ, popis…" className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 focus:border-slate-900 focus:outline-none text-sm" />
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="px-3 py-2.5 rounded-lg border border-slate-300 text-sm">
            <option value="all">Všechny kategorie</option>
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => setStatusFilter('all')} className={`px-3 py-2.5 rounded-lg text-xs font-semibold ${statusFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}>
            Vše ({items.length})
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-500">Načítám…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center rounded-xl bg-white border border-dashed border-slate-300 text-slate-500">Žádné požadavky.</div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] tracking-widest">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">ID</th>
                    <th className="px-4 py-3 text-left font-semibold">VYTVOŘENO</th>
                    <th className="px-4 py-3 text-left font-semibold">KATEGORIE</th>
                    <th className="px-4 py-3 text-left font-semibold">TYP</th>
                    <th className="px-4 py-3 text-left font-semibold">ZADAVATEL</th>
                    <th className="px-4 py-3 text-left font-semibold">DATUM AKCE</th>
                    <th className="px-4 py-3 text-left font-semibold">STAV</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const st = STATUS_STYLE[r.status] || STATUS_STYLE['nový'];
                    return (
                      <tr key={r.request_id} data-testid={`row-${r.request_number}`} onClick={() => setSelected(r)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                        <td className="px-4 py-3 font-mono text-xs">{r.request_number}</td>
                        <td className="px-4 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3">{r.category_label || CAT_NAME[r.category]}</td>
                        <td className="px-4 py-3">{r.request_type}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{r.requester_name}</div>
                          <div className="text-xs text-slate-500">{r.requester_role}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{r.event_date || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 ${st.bg} ${st.text}`}>{r.status}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {selected && <RequestDetailDrawer req={selected} onClose={() => setSelected(null)} onPatch={patchItem} onReply={sendReply} />}
    </div>
  );
};

const RequestDetailDrawer = ({ req, onClose, onPatch, onReply }) => {
  const [note, setNote] = useState(req.internal_note || '');
  const [assignee, setAssignee] = useState(req.assignee || '');
  const [replyMsg, setReplyMsg] = useState('');
  const [replyStatus, setReplyStatus] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="detail-drawer">
        <div className="p-6 border-b border-slate-200 flex justify-between items-start gap-4">
          <div>
            <div className="font-mono text-xs text-slate-500">{req.request_number}</div>
            <div className="text-xl font-black text-slate-900 mt-1">{req.category_label || CAT_NAME[req.category]} · {req.request_type}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold mb-2">STAV</div>
            <div className="flex flex-wrap gap-2">
              {REQUEST_STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`set-status-${s}`}
                  onClick={() => onPatch(req.request_id, { status: s })}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${req.status === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold mb-2">DETAIL</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {req.event_date && <><dt className="text-slate-500">Datum akce</dt><dd className="text-slate-900 font-semibold">{req.event_date}{req.event_time ? ` · ${req.event_time}` : ''}</dd></>}
              {req.location && <><dt className="text-slate-500">Prostor</dt><dd className="text-slate-900 font-semibold">{req.location}</dd></>}
              {req.number_of_people != null && <><dt className="text-slate-500">Počet osob</dt><dd className="text-slate-900 font-semibold">{req.number_of_people}</dd></>}
              {req.event_name && <><dt className="text-slate-500">Akce</dt><dd className="text-slate-900 font-semibold">{req.event_name}</dd></>}
            </dl>
            <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 whitespace-pre-wrap text-sm text-slate-800">{req.description}</div>
            {req.note && <div className="mt-2 text-xs italic text-slate-600">Poznámka zadavatele: {req.note}</div>}
          </section>

          <section>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold mb-2">ZADAVATEL</div>
            <div className="text-sm">
              <div className="font-semibold text-slate-900">{req.requester_name}</div>
              <div className="text-slate-600">{req.requester_role}</div>
              <div className="text-slate-600">{req.requester_email} · {req.requester_phone}</div>
            </div>
          </section>

          {(req.attachments || []).length > 0 && (
            <section data-testid="admin-attachments">
              <div className="text-[10px] tracking-widest text-slate-500 font-semibold mb-2">PŘÍLOHY ({req.attachments.length})</div>
              <ul className="space-y-2">
                {req.attachments.map((a, i) => {
                  const url = a.url?.startsWith('http') ? a.url : `${process.env.REACT_APP_BACKEND_URL}${a.url}`;
                  const isImage = (a.content_type || '').startsWith('image/');
                  return (
                    <li key={a.storage_path || i} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="flex items-center gap-3 min-w-0">
                        {isImage ? (
                          <img src={url} alt={a.filename} className="w-12 h-12 object-cover rounded border border-slate-300" />
                        ) : (
                          <div className="w-12 h-12 rounded bg-slate-200 border border-slate-300 flex items-center justify-center text-slate-600 text-[10px] font-bold">PDF</div>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{a.filename}</div>
                          <div className="text-[11px] text-slate-500">{Math.max(1, Math.round((a.size || 0) / 1024))} kB · {a.content_type}</div>
                        </div>
                      </div>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 shrink-0"
                      >
                        Otevřít
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold mb-2">INTERNÍ POZNÁMKA (nezobrazuje se zadavateli)</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Interní poznámka…" />
            <div className="mt-2 flex gap-2">
              <input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="Přiřazeno komu" className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              <button onClick={() => onPatch(req.request_id, { internal_note: note, assignee })} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold">Uložit</button>
            </div>
          </section>

          <section>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold mb-2">ODPOVĚDĚT ZADAVATELI (odešle e-mail)</div>
            <textarea value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} rows={4} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Text odpovědi (odešle se e-mailem zadavateli)…" />
            <div className="mt-2 flex gap-2 items-center">
              <select value={replyStatus} onChange={(e) => setReplyStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
                <option value="">Neměnit stav</option>
                {REQUEST_STATUSES.map((s) => <option key={s} value={s}>Změnit stav → {s}</option>)}
              </select>
              <button
                disabled={!replyMsg.trim()}
                onClick={() => { onReply(req.request_id, replyMsg, replyStatus || null); setReplyMsg(''); setReplyStatus(''); }}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-semibold"
              >
                Odeslat odpověď
              </button>
            </div>
            {(req.replies || []).length > 0 && (
              <div className="mt-4 space-y-2">
                {req.replies.map((rp, i) => (
                  <div key={i} className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-slate-800 whitespace-pre-wrap">
                    <div className="text-[10px] text-emerald-800 mb-1">{fmtDate(rp.at)} · {rp.actor}</div>
                    {rp.message}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold mb-2">HISTORIE</div>
            <ol className="space-y-1 text-xs text-slate-600">
              {(req.history || []).map((h, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-slate-400 font-mono shrink-0">{fmtDate(h.at)}</span>
                  <span className="text-slate-700"><strong>{h.actor}</strong> · {h.action}{h.detail ? ` — ${h.detail}` : ''}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
};

export default BeraniRequestsAdminPage;
